import React, { createContext, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import { colors } from '../../../core';
import Label from '../Label';

export interface RadioContextProps {
  name: string;
  selected: string;
  handleRadioChange?: (value: string) => void;
  position?: 'vertical' | 'horizontal';
}

export interface RadioGroupProps {
  children: React.ReactNode;
  initialValue: string;
  name: string;
  title?: string;
  onChange: (value: string) => void;
  required?: boolean;
  errorMessageOnDisable?: string;
  position?: 'vertical' | 'horizontal';
  labelSize?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
  noLabels?: boolean;
  column?: number;
}

export interface RadioInputProps {
  className?: string;
  disabled?: boolean;
  minWidth?: string;
  label: string;
  value: string;
  id: string;
  children?: React.ReactNode;
}

const RadioContext = createContext<RadioContextProps>({
  name: '',
  selected: '',
  position: 'vertical',
});

const InputContainer = styled.div<{ position?: 'vertical' | 'horizontal'; minWidth?: string }>`
  align-items: center;
  display: inline-flex;
  margin: ${({ position }) => (position && position === 'vertical' ? '9px 0' : '9px 32px 9px 0')};
  min-width: ${({ minWidth }) => minWidth || 'auto'};
  position: relative;
`;

const InputContainerForComponents = styled.div<{ position?: 'vertical' | 'horizontal' }>`
  align-content: center;
  display: flex;
  margin: ${({ position }) => (position && position === 'vertical' ? '9px 0' : '9px 32px 9px 0')};
  position: relative;
`;

const RadioWrapper = styled.div<{ position?: 'vertical' | 'horizontal'; column?: number }>`
  display: flex;
  flex-direction: ${({ position }) => (position && position === 'vertical' ? 'column' : 'row')};
  margin-left: ${({ position }) => (position && position === 'vertical' ? '20px' : '10px')};

  ${({ column }) =>
    column &&
    `
    display: grid;
    grid-template-columns: ${
      typeof column === 'string'
        ? column
        : column
        ? `repeat(${column}, ${(100 - 2 * (column - 1)) / column}%)`
        : 'repeat(1, 100%)'
    };
    column-gap: 2%;
  `}
`;

const Input = styled.input.attrs({
  type: 'radio',
})`
  -webkit-appearance: none;
  clip: rect(1px, 1px, 1px, 1px);
  height: 1px;
  margin: 0;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const InputLabel = styled.label`
  color: ${({ theme }) => theme.radioColor || colors.charcoal};
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontFamily || `Quicksand, san-serif`};
  font-size: ${({ theme }) => theme.fontSize || '14px'};
  line-height: ${({ theme }) => theme.lineHeight || 'normal'};
  font-weight: ${({ theme }) => theme.fontWeight || 600};
  padding-left: 25px;

  &::before,
  &::after {
    border-radius: 50%;
    content: '';
    display: block;
    position: absolute;
  }

  &::before {
    background-color: ${({ theme }) => theme.radioBackgroundColor || colors.white};
    border: 1px solid ${({ theme }) => theme.radioBorderColor || colors.smoke};
    height: ${({ theme }) => `${theme.radioSize}px` || '20px'};
    width: ${({ theme }) => `${theme.radioSize}px` || '20px'};
    top: 50%;
    left: 0;
    transform: translate(-50%, -50%);
  }

  &::after {
    height: ${({ theme }) => `${theme.radioSize - 8}px` || '12px'};
    width: ${({ theme }) => `${theme.radioSize - 8}px` || '12px'};
    top: 50%;
    left: 0;
    transform: translate(-50%, -50%);
  }

  ${Input}:checked + &::before {
    border: 1px solid ${({ theme }) => theme.radioBorderActiveColor || colors.primary};
  }

  ${Input}:checked + &::after {
    background-color: ${({ theme }) => theme.radioBackgroundActiveColor || colors.primary};
  }

  ${Input}:not(:disabled) + &:hover::before,
  ${Input}:focus + &::before {
    border: 1px solid ${({ theme }) => theme.radioHover || colors.primaryHover};
    box-shadow: 0 0 3px ${({ theme }) => theme.radioShadow || colors.primaryHover};
  }

  ${Input}:disabled + & {
    color: ${({ theme }) => theme.radioBorderColor || colors.smoke};
    cursor: default;
  }

  ${Input}:disabled:checked + &::before {
    border-color: ${colors.primaryDisabled};
  }

  ${Input}:disabled:checked + &::after {
    background-color: ${colors.primaryDisabled};
  }
`;

const RadioInput = ({ className, disabled, label, value, id, minWidth }: RadioInputProps) => {
  const { handleRadioChange, name, selected, position }: RadioContextProps =
    useContext(RadioContext);

  return (
    <InputContainer className={className} position={position} minWidth={minWidth}>
      <Input
        checked={selected === value}
        disabled={disabled}
        id={id}
        name={name}
        onChange={() => handleRadioChange && handleRadioChange(value)}
        value={value}
      />
      <InputLabel htmlFor={id}>{label || value}</InputLabel>
    </InputContainer>
  );
};

const ComponentContainer = styled.div`
  flex: 1;
`;

const RadioInputForComponents = ({ className, disabled, value, id, children }: RadioInputProps) => {
  const { handleRadioChange, name, selected, position }: RadioContextProps =
    useContext(RadioContext);

  return (
    <InputContainerForComponents className={className} position={position}>
      <Input
        checked={selected === value}
        disabled={disabled}
        id={id}
        name={name}
        onChange={() => handleRadioChange && handleRadioChange(value)}
        value={value}
      />
      <InputLabel htmlFor={id} />
      <ComponentContainer>{children}</ComponentContainer>
    </InputContainerForComponents>
  );
};

const RadioGroup = ({
  children,
  initialValue,
  name,
  title,
  required,
  onChange,
  position,
  column,
  labelSize,
  noLabels = false,
}: RadioGroupProps) => {
  const [selected, setSelected] = useState(initialValue);

  useEffect(() => {
    if (selected !== initialValue) {
      setSelected(initialValue);
    }
  }, [initialValue]);

  const handleRadioChange = (value: string) => {
    if (value === selected) {
      return;
    }
    setSelected(value);
    onChange(value);
  };

  return (
    <RadioContext.Provider value={{ name, selected, handleRadioChange, position }}>
      <div>
        {!noLabels && title && (
          <Label size={labelSize}>
            {title}
            {required && '*'}
          </Label>
        )}
        <RadioWrapper position={position} column={column}>
          {children}
        </RadioWrapper>
      </div>
    </RadioContext.Provider>
  );
};

RadioGroup.Input = RadioInput;
RadioGroup.RadioInputForComponents = RadioInputForComponents;

export default RadioGroup;
